import { Component, input, OnInit, ViewEncapsulation, TemplateRef, computed, signal, inject, effect, viewChild, DestroyRef } from '@angular/core'; // , OnDestroy
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { FastFetchDetails } from 'src/app/models/fast-fetch-details';
import { Integration } from 'src/app/models/integration';
import { IntegrationIdentifier } from 'src/app/models/integration-identifier';
import { IntegrationInformation } from 'src/app/models/integration-information';
import { User } from 'src/app/models/user';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { FastFetchService } from 'src/app/services/fastfetch.service';
import { ToastService } from 'src/app/services/toast.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { LoanNumberTableComponent } from 'src/app/features/lt-shared-ui/lt-grid-ui/components/loan-number-table/loan-number-table.component';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';
import { LTButtonToggleComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-button-toggle/lt-button-toggle.component';
import { LTPageLoadingMessageComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-page-loading-message/lt-page-loading-message.component';

@Component({
  selector: 'app-fastfetch',
  templateUrl: './fastfetch.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./fastfetch.component.scss'],
  providers: [ClientIntegrationService, FastFetchService, TypeConstantService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatTooltipModule,
    LoanNumberTableComponent,
    LTPngTableComponent,
    LTButtonToggleComponent,
    LTPageLoadingMessageComponent,
  ],
})
export class FastfetchComponent implements OnInit { // , OnDestroy
  private readonly fastFetchService = inject(FastFetchService);
  private readonly toastService = inject(ToastService);
  private readonly typeService = inject(TypeConstantService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  activeUser: User;
  pageLoading = signal<boolean>(true);

  //#region Constants
  approvedDomains: string[] = ['ncino.com', 'lbaware.com', 'simplenexus.com'];
  csFastFetchRoles: string[] = [
    'LOSTalker.Integration.CompenSafe.FastFetch',
    'LOSTalker.Integration.CompenSafe',
  ];

  lgFastFetchRoles: string[] = [
    'LOSTalker.Integration.LimeGear.FastFetch',
    'LOSTalker.Integration.LimeGear',
  ];
  //#endregion
  useLambdaProcessing = false;

  //#region Inputs
  integration = input<Integration>(null);
  isInTab = input<boolean>(false);
  integrationInfo = input<IntegrationInformation[]>([]);

  //#endregion

  //#region Progress-Dialog-Props
  loanProgressDialog = viewChild<TemplateRef<any>>('loanProgressDialog');
  progressBarValue = signal<number>(60);
  progressBarBufferValue = signal<number>(99);
  loansBeingProcessed = signal<boolean>(true);
  loansProcessedResponse = signal<string>('');
  //#endregion

  //#region Override-Email Props
  private overrideEmailSignal = signal<boolean>(false);
  overrideEmail = computed(() => this.overrideEmailSignal());
  emailFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.email,
  ]);

  //#endregion

  loanNumberTable = viewChild(LoanNumberTableComponent);
  integrationTable = viewChild(LTPngTableComponent);

  selectedIntegration = signal<IntegrationInformation | null>(null);
  filteredGroup = signal<IntegrationInformation[]>(null); // integration information filtered by destination
  allOriginalGroup = signal<IntegrationInformation[]>(null); // all integration information
  
  //#region Destination
  hasDestinations = signal<boolean>(false);
  allDestinations = signal<DestinationEndpoint[] | null>(null);
  filteredDestinations = signal<DestinationEndpoint[] | null>(null);
  public selectedDestination = signal<DestinationEndpoint>(null);
  //#endregion

  public formGroup: UntypedFormGroup;
  
  constructor(user: User) {
    this.activeUser = user;
    effect(() => {
      if (this.settingsReady() === false) {
        this.setupGrid();
      }
    });
  }

  ngOnInit(): void {
    this.getDestinations();
    // handle input
    if (this.integrationInfo() && this.integrationInfo().length > 0) {
      const info = this.integrationInfo();
      this.filteredGroup.set(info);
      this.allOriginalGroup.set(info);
      this.gridLoading.set(false);

      if (this.integrationInfo().length == 1) {
        this.selectedIntegration.set(this.integrationInfo()[0]);
      }
    }
  }

  //#region Destinations Setup
  getDestinations(): void {
    this.typeService.getAllDestinationEndpoints()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.allDestinations.set(r);
          this.processFilteredDestinations();
          this.pageLoading.set(false);
        },
        error: (_error) => {
          this.pageLoading.set(false);
        },
      });
  }

  processFilteredDestinations(): void {
    if (this.integrationInfo() && this.integrationInfo().length > 0) {
      if (this.selectedIntegration() != null) {
        const foundDestination = this.allDestinations().find(x => x.destinationEndpointId == this.selectedIntegration().destinationId);
        if (foundDestination){
          this.selectedDestination.set(foundDestination);
          this.filteredDestinations.set([foundDestination]);
        }
        
      }
      else {
        const destArr = this.integrationInfo().map(i => i.destinationId);
        this.filteredDestinations.set(this.allDestinations().filter(i => destArr.indexOf(i.destinationEndpointId) != -1));
      }
    }
    else {
      this.filteredDestinations.set(this.allDestinations().filter(d => d.fastFetchEnabled == true));
    }

    if (!this.activeUser.checkAccess(this.csFastFetchRoles)) {
      const current = this.filteredDestinations();
      if (current) {
        const filtered = current.filter(d => d.endpointName !== 'CSDataSwap');
        this.filteredDestinations.set(filtered);
      }
    }

    if (!this.activeUser.checkAccess(this.lgFastFetchRoles)) {
      const current = this.filteredDestinations();
      if (current) {
        const filtered = current.filter(d => d.endpointName !== 'LGDataSwap');
        this.filteredDestinations.set(filtered);
      }
    }

    if (!this.activeUser.checkAccess(['LOSTalker.Integration'])) {
      const current = this.filteredDestinations();
      if (current) {
        const filtered = current.filter(d => d.endpointName !== 'Surefire3');
        this.filteredDestinations.set(filtered);
      }
    }

    const finalDestinations = this.filteredDestinations();
    if (finalDestinations && finalDestinations.length == 1) {
      this.selectedDestination.set(finalDestinations[0]);
    }
    else if (!finalDestinations || finalDestinations.length == 0) {
      this.toastService.toastCreate('You do not have credentials to FastFetch loans.', 'Warning', { autoClose: true });
    }

    this.hasDestinations.set(true);

    if (!this.integrationInfo() || (this.integrationInfo() && this.integrationInfo().length < 1)) {
      this.getAllFFInfo();
    }
  }

  //#endregion

  getAllFFInfo(): void {
    const id = this.selectedDestination() != null ? (this.selectedDestination().destinationEndpointId ?? null) : null;
    this.fastFetchService.getAllFastFetchIntegrationInformationByDestId(id)
    .pipe(passUntilDestroyed(this.destroyRef))
    .subscribe(intList => {
      intList = intList.filter(i => i.serverId != '0' && i.locked == false);
      this.filteredGroup.set(intList);
      this.allOriginalGroup.set(intList);
      this.gridLoading.set(false);
    });
  }

  changeClient(): void {
    this.selectedIntegration.set(null);
    this.updatePgGrid();
  }

  public submitFastFetchRequest(): void {
    if (!this.selectedIntegration() || this.selectedIntegration() == null || this.selectedIntegration() == undefined) {
      this.toastService.toastCreate('Invalid Integration Identifier.', 'Warning', { autoClose: true, keepAfterRouteChange: false });
    }
    else {
      const details = new FastFetchDetails();

      details.identifier = new IntegrationIdentifier();
      const i = this.selectedIntegration();
      details.identifier.integrationId = i.integrationId;
      details.identifier.destinationId = i.destinationId;
      details.identifier.destinationName = i.destinationName;
      details.identifier.identifierIdValue = i.identifierIdValue;
      details.identifier.identifierNameValue = i.identifierNameValue;
      details.identifier.identifierKeyValue = i.identifierKeyValue;
      details.identifier.createdDate = i.identifierCreatedDate;
      details.identifier.updatedDate = i.identifierUpdatedDate;
      details.identifier.canFastFetch = i.canFastFetch;
      details.identifier.canDataSwap = i.canDataSwap;
      details.identifier.subscriptionName = i.subscriptionName;

      const loanStrings = this.ffLoansSignal().map(l => l.trim());
      details.loanNumbers = loanStrings;
      details.application = i.destinationName;
      details.processInLambdas = this.useLambdaProcessing;

      details.integration = new Integration(i);

      let email = this.activeUser.userName;
      if (this.overrideEmail()) {
        if (this.emailFormControl.valid) {
          email = this.emailFormControl.value;
        }
        else {
          this.toastService.toastCreate('Override email is not valid, sending update to original users email address.', 'Warning', { autoClose: true });
        }
      }

      details.email = email;

      const emailParts = details.email.split('@');
      const domain = emailParts[emailParts.length - 1].toLowerCase();

      if (this.approvedDomains.includes(domain)) {
        this.loansBeingProcessed.set(true);
        this.openLoansProgressDialog();
        this.fastFetchService.postFastFetchLoans(details)
          .pipe(passUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (_data) => {
              this.toastService.toastCreate('Loans sent to be processed', 'Success', { autoClose: true });
              this.loansProcessedResponse.set('Loans sent to be processed');
              this.loansBeingProcessed.set(false);

            },
            error: (_error) => {
              this.toastService.toastCreate('FastFetch POST Failed:' + _error.message, 'Warning', { autoClose: true });
              this.loansProcessedResponse.set('FastFetch POST Failed:' + _error.message);
              this.loansBeingProcessed.set(false);
            },
          });
      }
      else {
        var s = 'Email is not within NCino domain.';
        this.toastService.toastCreate(s, 'Warning', { autoClose: true });
      }
    }

  }

  public clearFastFetchLoanGrid(): void {
    this.loanNumberTable().clearAll();
  }

  public clearFastFetchFormData(): void {
    this.loanNumberTable().clearAll();

    this.filteredGroup.set([]);
    this.selectedIntegration.set(null);
    this.selectedDestination.set(null);
    this.gridLoading.set(true);
  }

  changeOverrideEmailCheck() {
    this.overrideEmailSignal.update(current => !current);
  }

  openLoansProgressDialog() {
    this.dialog.open(this.loanProgressDialog());
  }

  getSNEmail(email: string): string {
    let rc = email;
    if (email.indexOf('@lbaware.com') > -1) {
      const emailName = email.split('@')[0];
      const nameParts = emailName.split('.');
      rc = nameParts[0].substring(0, 1) + nameParts[1] + '@simplenexus.com';
    }

    return rc;
  }

  onLoansChange(value: string[]): void {
    this.ffLoansSignal.set(value);
  }

  //#region Properties-Methods for Dest Toggle
  // Computed signal for destination toggle options
  destinationOptions = computed(() => {
    return this.filteredDestinations()?.map(destination => ({
      value: destination.destinationEndpointId,
      label: destination.displayName,
      disabled: false,
    })) || [];
  });
  // Signal for selected destination value
  selectedDestinationValue = signal<number | null>(null);

  onDestinationChange(destinationId: number): void {
    this.selectedIntegration.set(null);
    this.selectedDestination.set(this.filteredDestinations().find(d => d.destinationEndpointId === destinationId));
    
    if (this.selectedDestination()) {
      this.selectedDestinationValue.set(destinationId);
      
      const filtered = this.allOriginalGroup().filter(x => x.destinationId == this.selectedDestination().destinationEndpointId);
      this.filteredGroup.set(filtered);
      this.updatePgGrid();
    }
  }

  //#endregion

  //#region Client Table
  public gridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);
  public settingsReady = signal<boolean>(false);
  private ffLoansSignal = signal<string[]>([]);

  get clientGridData(): IntegrationInformation[] {
    if (this.selectedIntegration())
      return this.filteredGroup().filter(i => i.integrationId == this.selectedIntegration().integrationId);
    else if (this.filteredGroup() && this.filteredGroup().length > 0)
      return this.filteredGroup();
    return [];
  }

  setupGrid(): void {
    this.gridSettings.set(new GridSettings({
      data: this.clientGridData,
      reorderableColumns: false,
      resizableColumns: false,
      loading: this.gridLoading,
      columns: [
        new GridColumnData({ field: 'identifierIdValue', header: 'Company ID', width: '75px', minWidth: '50px'}),
        new GridColumnData({ field: 'identifierNameValue', header: 'Company Name', width: '300px', minWidth: '50px'}),
        new GridColumnData({ field: 'integrationName', header: 'Integration Name', width: '300px', minWidth: '50px'}),
        new GridColumnData({ field: 'dataSourceType', header: 'Data Source Type', width: '150px', minWidth: '50px'}),
        new GridColumnData({ field: 'destinationName', header: 'Destination', width: '100px', minWidth: '50px'}),
        new GridColumnData({ field: 'serverId', header: 'Server', width: '75px', minWidth: '50px'}),
      ],
      sortMode: 'multiple',
      multisort: [
        {
          field: 'integrationName',
          order: 1,
        },
      ],
      dataKey: 'integrationId',
      selection: {
        enabled: true,
        mode: 'single',
        showSelectAll: false,
        text: '',
        selectionType: 'icon',
        iconClass: 'pi pi-star-fill',
        preselectedItems: this.preselectedIntegrations(),
      },
    }));
    this.settingsReady.set(true);
  }

  preselectedIntegrations = computed(() => {
    if (this.selectedIntegration()){
      const i = this.selectedIntegration();
      const arr = [];
      arr.push(i);
      return arr;
    }
    return [];
  });

  updatePgGrid(): void {
    this.gridSettings.set(new GridSettings({
      ...this.gridSettings(),
      data: this.clientGridData,
      loading: this.gridLoading,
      selection: {
        enabled: true,
        mode: 'single',
        showSelectAll: false,
        text: '',
        selectionType: 'icon',
        iconClass: 'pi pi-star-fill',
        preselectedItems: this.preselectedIntegrations(),
      },
    }));
  }

  handlePgGridSelection = (selectedRows: any): void => {
    this.selectedIntegration.set(selectedRows[0]);
    this.updatePgGrid();
  };
  //#endregion
}
